import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Splitter } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../LoadingPanel";
import { AdministratorVenueEvent } from "./VenueEvent";
import { Checkbox, TextBox, InputClearValue  } from "@progress/kendo-react-inputs";
import { Pager } from "@progress/kendo-react-data-tools";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Icon } from "@progress/kendo-react-common";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { ScreenSize } from '../../../app/ScreenSize';
import { GetScreenSize } from '../../../app/GetScreenSize';
import { getCSRFToken } from '../../../app/getCSRFToken';
import useSpeechToText from 'react-hook-speech-to-text';

import './VenueEvents.scss';

export const AdministratorVenueEvents = () => {
    const dispatch = useDispatch();

    const [venueEvents, setVenueEvents] = useState([]);
    const [venueEventsPagerData, setVenueEventsPagerData] = useState({
        total: 0,
        skip: 0,
        take: 1
    });
    const [selectedVenueEventKey, setSelectedVenueEventKey] = useState(undefined);
    const [venueEvent, setVenueEvent] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [doCreateNewVenueEvent, setDoCreateNewVenueEvent] = useState(false);
    const [venueEventNameFilterValue, setVenueEventNameFilterValue] = useState("");
    const [showPastEventsVenueEventsValue, setShowPastEventsVenueEventsValue] = useState(false);
    const [venueEventLoading, setVenueEventLoading] = useState(false);
    
    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [ticketingWebsitesFilterOptions, setTicketingWebsitesFilterOptions] = useState([defaultTicketingWebsite]);
    const [ticketingWebsiteIdFilterValue, setTicketingWebsiteIdFilterValue] = useState(undefined);
    const [venueEventStatusTypesFilterValue, setVenueEventStatusTypesFilterValue] = useState([]);

    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);

    const typingTimeout = useRef();
    const typingTimeoutDelay = 500; //Milliseconds

    const {
        error,
        results,
        isRecording,
        startSpeechToText,
        stopSpeechToText,
    } = useSpeechToText({
        timeout: 1000
    });

    const standardPanes = [
        {
            size: "340px",
            min: "5%",
            resizable: false,
        },
        {}
    ];

    const getMobilePanes = () => {
        if (!venueEvent && doCreateNewVenueEvent === false) {
            return [
                {
                    size: "100%",
                    resizable: false,
                },
                {
                    collapsed: true,
                }
            ];
        }

        return [
            {
                size: "100%",
                resizable: false,
                collapsed: true,
            },
            {
                              
            }
        ];
    }

    const renderVenueEventListItem = (props) => {
        let item = props.dataItem;
        
        return (
            <div className={"venueEventListItem" + (item.venueEventKey === selectedVenueEventKey ? " venueEventListItemSelected" : "")}
                onClick={() => selectVenueEvent(item.venueEventKey)}
                onDoubleClick={() => openTicketingPage(item.ticketingUrl)}>
                <div className="venueEventListPrimaryText">
                    {item.header} - <span className="text-primary text-nowrap">{item.venueEventStatusTypeName}</span>                 
                </div>
                <div className="venueEventListSecondaryText">                    
                    {ticketingWebsitesFilterOptions.find(element => element.ticketingWebsiteId === item.ticketingWebsiteId)?.name}
                    &nbsp;|&nbsp;
                    {item.venueName}                    
                </div>       
                <div className="venueEventListSecondaryText"> 
                    {item.eventDate}                    
                </div> 
            </div>
        );
    }

    const selectVenueEvent = (venueEventKey) => {
        getVenueEvent(venueEventKey);
    }

    const openTicketingPage = (ticketingUrl) => {
        if (!ticketingUrl) {
            return;
        }

        window.open(ticketingUrl, "_blank", "noreferrer");
    }

    const createNewVenueEvent = () => {
        setVenueEvent(undefined);
        setSelectedVenueEventKey(null);
        setVenueEventLoading(false);
        setDoCreateNewVenueEvent(true);

        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue, venueEventsPagerData.skip, venueEventsPagerData.take);
    }

    const clearVenueEvent = () => {
        setVenueEvent(undefined);
        setSelectedVenueEventKey(null);
        setVenueEventLoading(false);
        setDoCreateNewVenueEvent(false);

        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue, venueEventsPagerData.skip, venueEventsPagerData.take);
    }

    const getVenueEvent = (venueEventKey) => {
        setSelectedVenueEventKey(venueEventKey);
        setVenueEventLoading(true);
        setDoCreateNewVenueEvent(false);

        const dataItem = {
            venueEventKey: venueEventKey,
        };

        fetch(`/api/administrator/getvenueevent`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEvent(result);
                setVenueEventLoading(false);

                getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue, venueEventsPagerData.skip, venueEventsPagerData.take);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setVenueEvent(undefined);
                setVenueEventLoading(false);
            });
    }

    const getVenueEvents = (name, showPastEvents, ticketingWebsite, statusTypes, skip, take) => {
        if (!skip) {
            skip = 0;
        }

        if (!take) {
            take = 1;
        }

        const pageNumber = (skip / take) + 1;

        const statusIds = statusTypes.map(x => x.venueEventStatusTypeId);

        const dataItem = {
            name: name,
            showPastEvents: showPastEvents,
            pageNumber: pageNumber,
            ticketingWebsiteId: ticketingWebsite?.ticketingWebsiteId !== "" ? ticketingWebsite?.ticketingWebsiteId : null,
            venueEventStatusTypeIds: statusIds,
        };

        fetch(`/api/administrator/venueevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setLoading(false);
                setVenueEvents(result.venueEvents);
                setVenueEventsPagerData({
                    total: result.totalVenueEventCount,
                    skip: (result.pageNumber - 1) * result.venueEventsPerPage,
                    take: result.venueEventsPerPage
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                
                setLoading(false);
                setLoadingError(error.message);
            });        
    }

    const getTicketingWebsites = () => {
        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(result);
                setTicketingWebsitesFilterOptions(allTicketingWebsites);
                setTicketingWebsiteIdFilterValue(defaultTicketingWebsite);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEventStatusTypes(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const handleVenueEventPageChange = (event) => {
        const { skip, take } = event;

        setVenueEventsPagerData({
            total: venueEventsPagerData.total,
            skip: skip,
            take: take
        });

        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue, skip, take);
    };

    const venueEventNameFilterValueChangedEvent = (event) => {
        const value = event.value;
        venueEventNameFilterValueChanged(value);        
    }

    const venueEventNameFilterValueChanged = (value) => {
        clearTimeout(typingTimeout.current);
        setVenueEventNameFilterValue(value);

        typingTimeout.current = setTimeout(() => {
            getVenueEvents(value, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue);   
        }, typingTimeoutDelay);
    }

    const venueEventNameClearFilterValue = () => {
        setVenueEventNameFilterValue("");
        getVenueEvents("", showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue);
    }

    const venueEventStatusTypesFilterValueChanged = (event) => {
        setVenueEventStatusTypesFilterValue([...event.value]);
        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, event.value);
    }

    const showPastEventsVenueEventsValueChanged = (event) => {
        const value = event.value;

        setShowPastEventsVenueEventsValue(value);
        getVenueEvents(venueEventNameFilterValue, value, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue);
    }

    const ticketingWebsiteFilterValueChanged = (event) => {
        const value = event.value;
        
        setTicketingWebsiteIdFilterValue(value);
        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, value, venueEventStatusTypesFilterValue);
    }

    useEffect(() => {
        document.title = "Venue Events";

        getTicketingWebsites();
        getVenueEventStatusTypes();
        getVenueEvents(venueEventNameFilterValue, showPastEventsVenueEventsValue, ticketingWebsiteIdFilterValue, venueEventStatusTypesFilterValue);
    }, []);

    useEffect(() => {
        if (!isRecording) {
            if (results.length > 0) {
                const speechValue = results.pop();
                venueEventNameFilterValueChanged(speechValue); 
            }
        }
    }, [isRecording]);
        
    return (
        <Splitter                
            panes={GetScreenSize() === ScreenSize.Mobile ? getMobilePanes() : standardPanes}
        >                
            <div>
                <div className="venueEventListFilterHeader">
                    <Button icon="plus" themeColor={"primary"} onClick={createNewVenueEvent}>Create New Event</Button>
                </div>
                <div className="venueEventListFilter">
                    <div className="d-flex flex-row">
                        <TextBox
                            type={"text"}
                            maxLength={"50"}
                            value={venueEventNameFilterValue}
                            onChange={venueEventNameFilterValueChangedEvent}
                            placeholder={"Search part of event name"}   
                            suffix={() => (
                                <>
                                    {venueEventNameFilterValue !== "" && (
                                        <InputClearValue onClick={venueEventNameClearFilterValue}>
                                            <Icon name="x" />
                                        </InputClearValue>
                                    )}                                
                                </>
                            )}
                        />

                        {!error && <Button className={isRecording ? "recordButton recording pt-2 pb-0 ms-2" : "recordButton pt-2 pb-0 ms-2"} iconClass="fa-solid fa-microphone" onClick={isRecording ? stopSpeechToText : startSpeechToText} />}
                    </div>
                    <div>
                        <DropDownList
                            data={ticketingWebsitesFilterOptions}
                            value={ticketingWebsiteIdFilterValue}
                            textField="name"
                            dataItemKey="ticketingWebsiteId"
                            onChange={ticketingWebsiteFilterValueChanged}
                        />
                    </div>
                    <div>
                        <MultiSelect
                            data={venueEventStatusTypes}
                            textField="name"
                            dataItemKey="venueEventStatusTypeId"
                            size="medium"
                            placeholder="Filter by status"
                            value={venueEventStatusTypesFilterValue}
                            onChange={venueEventStatusTypesFilterValueChanged}
                        />
                    </div>
                    <div>
                        <Checkbox
                            label={"Show past events"}
                            onChange={showPastEventsVenueEventsValueChanged}
                            value={showPastEventsVenueEventsValue}
                        />
                    </div>
                </div>
                {loading ? <LoadingPanel /> : (loadingError === "" ? (
                    <>
                        <ListView
                            data={venueEvents}
                            item={renderVenueEventListItem}
                        />
                        <Pager
                            skip={venueEventsPagerData.skip}
                            take={venueEventsPagerData.take}
                            total={venueEventsPagerData.total}
                            buttonCount={0}
                            info={true}
                            type={"numeric"}
                            previousNext={true}
                            onPageChange={handleVenueEventPageChange}
                        />
                    </>) : <div></div>)}
            </div>
            <AdministratorVenueEvent
                venueEvent={venueEvent}
                loading={venueEventLoading}
                createNewVenueEvent={doCreateNewVenueEvent}
                cancel={clearVenueEvent}
                selectVenueEvent={selectVenueEvent}
                ticketingWebsites={ticketingWebsites}
                venueEventStatusTypes={venueEventStatusTypes}
            />
        </Splitter>
    );
}